import React, { useMemo } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  ComposedChart,
} from "recharts";

function money(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function formatMillions(value) {
  return `${(Number(value || 0) / 1_000_000).toFixed(2)}M`;
}

function formatThousands(value) {
  return `${(Number(value || 0) / 1_000).toFixed(2)}K`;
}

function formatFullMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getLastTenMonthsIncludingCurrent() {
  const start = moment().startOf("month").subtract(9, "months");

  return Array.from({ length: 10 }, (_, index) => {
    const current = start.clone().add(index, "months");

    return {
      monthKey: current.format("YYYY-MM"),
      monthLabel: current.format("MMM YYYY"),
      start: current.clone().startOf("month").format("YYYY-MM-DD"),
      end: current.clone().endOf("month").format("YYYY-MM-DD"),
    };
  });
}

function calculateInvestmentRow(investment, interestStart, interestEnd) {
  let cost = money(investment.cost);
  const impairment = money(investment.impaired_amount);
  const total_interest = money(investment.total_interest);
  const daily_interest = money(investment.daily_interest);

  const purchase_date = moment(investment.purchase_date).format("YYYY-MM-DD");
  const maturity_date = moment(investment.maturity_date).format("YYYY-MM-DD");

  const tenure = Number(investment.tenure || 0);
  const rate_type = investment.rate_type;

  let status = moment(maturity_date).isAfter(moment()) ? "Active" : "Matured";

  let days_earned = moment().diff(moment(purchase_date), "days") + 1;
  let interest_charged = days_earned * daily_interest;
  let days_unearned = tenure - days_earned;
  let cumulative_interest = days_earned * daily_interest;
  let interest_unearned = days_unearned * daily_interest;

  let fair_value =
    rate_type === "interest rate"
      ? cost + cumulative_interest - impairment
      : cost - impairment;

  if (status === "Matured") {
    days_earned = moment(maturity_date).diff(moment(purchase_date), "days");
    days_unearned = 0;
    cumulative_interest = days_earned * daily_interest;
    interest_unearned = 0;
    interest_charged = 0;

    fair_value =
      rate_type === "interest rate"
        ? cost + cumulative_interest - impairment
        : cost - impairment;
  }

  if (interestStart && interestEnd) {
    const start = moment(interestStart);
    const end = moment(interestEnd);
    const purchase = moment(purchase_date);
    const maturity = moment(maturity_date);

    if (
      purchase.isBefore(start) &&
      maturity.isBefore(start) &&
      status === "Matured"
    ) {
      cumulative_interest = cost;
      interest_unearned = 0;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (end.isBefore(purchase)) {
      cost = 0;
      fair_value = 0;
      interest_unearned = 0;
      cumulative_interest = 0;
      days_earned = 0;
      days_unearned = 0;
      interest_charged = 0;
      status = "investment was not available then";
    }

    if (purchase.isSameOrBefore(start) && maturity.isAfter(end)) {
      const diff = end.diff(start, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (
      purchase.isBefore(start) &&
      maturity.isSameOrAfter(start) &&
      maturity.isSameOrBefore(end)
    ) {
      const diff = maturity.diff(start, "days");
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = tenure - cummDateDiff;
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = cummDateDiff * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = diff * daily_interest;
    }

    if (maturity.isBefore(start)) {
      interest_unearned = 0;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (
      purchase.isAfter(start) &&
      purchase.isSameOrBefore(end) &&
      maturity.isAfter(end)
    ) {
      const diff = end.diff(purchase, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (purchase.isSameOrAfter(start) && maturity.isSameOrBefore(end)) {
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = 0;
      interest_unearned = 0;
      cumulative_interest = tenure * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      const diff = maturity.diff(purchase, "days");
      interest_charged = diff * daily_interest;
    }
  }

  return {
    code: investment.code,
    certificate_number: investment.certificate_number,
    institution: investment.institution,
    investment_type: investment.investment_type,
    regulator: investment.regulator,
    rate: investment.rate,
    purchase_date,
    maturity_date,
    tenure,
    days_earned,
    days_unearned,
    cost,
    cumulative_interest,
    interest_unearned,
    impairment,
    fair_value,
    total_interest,
    interest_charged,
    daily_interest,
    rate_type,
    status,
  };
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  const costItem = payload.find((item) => item.dataKey === "cost");
  const interestItem = payload.find(
    (item) => item.dataKey === "interestCharged",
  );

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1px solid #e2e8f0",
        backgroundColor: "#fff",
        px: 1.6,
        py: 1.3,
        boxShadow: "0 12px 30px rgba(15,23,42,0.12)",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 900,
          color: "#1e293b",
          mb: 0.8,
        }}
      >
        {label}
      </Typography>

      <Typography sx={{ fontSize: 12, color: "#355c80", fontWeight: 800 }}>
        Cost: {formatMillions(costItem?.value)}
      </Typography>

      <Typography sx={{ fontSize: 12, color: "#17b859", fontWeight: 800 }}>
        Interest Charged: {formatThousands(interestItem?.value)}
      </Typography>

      <Box sx={{ mt: 0.8, pt: 0.8, borderTop: "1px solid #e2e8f0" }}>
        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
          Full Cost: {formatFullMoney(costItem?.value)}
        </Typography>

        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
          Full Interest: {formatFullMoney(interestItem?.value)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function LineChartHome() {
  const approvedInvestments = useSelector((state) => state.approvedInvestment);

  const months = useMemo(() => getLastTenMonthsIncludingCurrent(), []);

  const chartData = useMemo(() => {
    if (!Array.isArray(approvedInvestments)) return [];

    return months.map((month) => {
      const detailedRows = approvedInvestments
        .filter((investment) => {
          const purchaseDate = moment(investment.purchase_date);
          return purchaseDate.isSameOrBefore(moment(month.end));
        })
        .map((investment) =>
          calculateInvestmentRow(investment, month.start, month.end),
        )
        .filter((row) => Math.abs(Number(row.interest_charged || 0)) > 0.0001);

      const totalCost = detailedRows.reduce(
        (sum, row) => sum + money(row.cost),
        0,
      );

      const totalInterestCharged = detailedRows.reduce(
        (sum, row) => sum + money(row.interest_charged),
        0,
      );

      return {
        month: month.monthLabel,
        monthKey: month.monthKey,
        start: month.start,
        end: month.end,
        investmentCount: detailedRows.length,
        cost: totalCost,
        costInMillions: totalCost / 1_000_000,
        interestCharged: totalInterestCharged,
        interestChargedInThousands: totalInterestCharged / 1_000,
      };
    });
  }, [approvedInvestments, months]);

  return (
    <Box
      sx={{
        width: "50%",
        backgroundColor: "transparent",
        p: { xs: 1.5, md: 2 },
      }}
    >
      {/* Chart */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          width: "100%",
          maxWidth: "100%",
          borderRadius: "18px",
          border: "1px solid #e2e8f0",
          p: { xs: 1.5, md: 2 },
          boxSizing: "border-box",
          boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
        }}
      >
        <Box
          sx={{
            mb: 1.7,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 900,
                color: "#1e293b",
              }}
            >
              Cost and Interest Charged Trend
            </Typography>
          </Box>

          <Box
            sx={{
              borderRadius: "999px",
              backgroundColor: "rgba(53,92,128,0.10)",
              color: "#355c80",
              px: 1.3,
              py: 0.6,
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            Past 10 Months
          </Box>
        </Box>

        {chartData.length > 0 ? (
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              height: 360,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{
                  top: 20,
                  right: 20,
                  left: 8,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  yAxisId="cost"
                  tickFormatter={formatMillions}
                  tick={{ fontSize: 11, fill: "#355c80" }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />

                <YAxis
                  yAxisId="interest"
                  orientation="right"
                  tickFormatter={formatThousands}
                  tick={{ fontSize: 11, fill: "#17b859" }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />

                <Tooltip content={<CustomTooltip />} />

                <Legend
                  verticalAlign="top"
                  height={36}
                  wrapperStyle={{
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                />

                <Area
                  yAxisId="cost"
                  type="monotone"
                  dataKey="cost"
                  name="Total Cost"
                  fill="#355c80"
                  fillOpacity={0.08}
                  stroke="none"
                />

                <Line
                  yAxisId="cost"
                  type="monotone"
                  dataKey="cost"
                  name="Total Cost"
                  stroke="#355c80"
                  strokeWidth={3}
                  dot={{
                    r: 4,
                    strokeWidth: 2,
                    fill: "#fff",
                    stroke: "#355c80",
                  }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  yAxisId="interest"
                  type="monotone"
                  dataKey="interestCharged"
                  name="Interest Charged"
                  stroke="#17b859"
                  strokeWidth={3}
                  dot={{
                    r: 4,
                    strokeWidth: 2,
                    fill: "#fff",
                    stroke: "#17b859",
                  }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              height: 280,
              borderRadius: "16px",
              border: "1px dashed #cbd5e1",
              backgroundColor: "#f8fafc",
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: "#334155",
                }}
              >
                No trend data found
              </Typography>

              <Typography sx={{ mt: 0.4, fontSize: 12, color: "#64748b" }}>
                Approved investments were not found for the past 10 months.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
